import type { EditorV2HostApi } from "@docit/editor-react";
import {
  ArrowSquareOut,
  ChatCircleDots,
  ChatsCircle,
  ClockCounterClockwise,
  DotsThree,
  LinkSimple,
  Note,
  PencilSimple,
  Sparkle,
  UsersThree,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { actionsToMenuItems } from "~/actions/adapters/menu";
import { useActionContext } from "~/actions/context";
import { documentAiActions } from "~/actions/definitions/ai";
import { documentRowActions } from "~/actions/definitions/documents";
import { useApi } from "~/api/provider";
import {
  useAccessibleCollection,
  useAvailableThinkers,
  useDocument,
  useMentionSuggestions,
  useSaveDocumentBody,
  useScopedAiActions,
  useSculptLatest,
  useUpdateDocument,
} from "~/api/queries";
import { debateHref } from "~/api/researchTypes";
import { ABILITY, type Document, documentStatus } from "~/api/types";
import { DocumentDrawer } from "~/features/conversations/DocumentDrawer";
import { documentPorts } from "~/features/conversations/ports/editorPorts";
import { runDoAction, SELECTION_DO_ACTIONS } from "~/features/documents/aiDoActions";
import { takeCompose } from "~/features/documents/composeHandoff";
import { DescribesCodePanel } from "~/features/documents/DescribesCodePanel";
import { DocumentBreadcrumbs } from "~/features/documents/DocumentBreadcrumbs";
import { DocumentConnections } from "~/features/documents/DocumentConnections";
import { DocumentTasksPanel } from "~/features/documents/DocumentTasksPanel";
import { EditorLaneSwitch } from "~/features/documents/EditorLaneSwitch";
import { EditorV2Host } from "~/features/documents/EditorV2Host";
import { DocumentEditor } from "~/features/documents/editor/DocumentEditor";
import { SelectionActionsDrawer } from "~/features/documents/editor/SelectionActionsDrawer";
import { useSelectionActions } from "~/features/documents/editor/useSelectionActions";
import { registerQuoteProvider } from "~/features/documents/editor/widgetProviders";
import { PinButton } from "~/features/documents/PinButton";
import { StarButton } from "~/features/documents/StarButton";
import { ComposeCard } from "~/features/documents/sculpt/ComposeCard";
import { SculptDialog } from "~/features/documents/sculpt/SculptDialog";
import { SculptProposalCard } from "~/features/documents/sculpt/SculptProposalCard";
import { SculptRevertRow } from "~/features/documents/sculpt/SculptRevertRow";
import { EntityFlowsPanel } from "~/features/flows/EntityFlowsPanel";
import { NudgeChip } from "~/features/nudges/NudgeChip";
import { NudgePickerHost } from "~/features/nudges/NudgePickerHost";
// The module path, not the package index: importing through the index would pull the
// /subscriptions scene into this chunk and defeat its lazy route.
import { SubscribeButton } from "~/features/subscribe/SubscribeButton";
import { TaskizeHost } from "~/features/taskize/TaskizeHost";
import { useThinkerDock } from "~/features/work/piles/pilesApi";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { Tooltip } from "~/foundation/components/Tooltip";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { formatDateTime } from "~/foundation/utils/time";
import { usePolicy } from "~/policies/usePolicy";
import { useShellStore } from "~/shell/store";

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  archived: "Archived",
  deleted: "In trash",
  published: "",
};

/**
 * The platform's documents open with an H1 repeating the title (often an empty "# ").
 * The scene renders the title itself, so the body drops that one line — and remembers it
 * did, because a save must put the heading back or the convention erodes one edit at a time.
 */
function splitBody(text: string): { body: string; hadHeading: boolean } {
  const lines = text.split("\n");
  const first = lines.findIndex((line) => line.trim().length > 0);
  if (first !== -1 && /^#(?!#)/.test((lines[first] ?? "").trim())) {
    return {
      body: lines
        .slice(first + 1)
        .join("\n")
        .trim(),
      hadHeading: true,
    };
  }
  return { body: text.trim(), hadHeading: false };
}

export function DocumentScene({
  documentId,
  mode = "read",
  panel,
  panelEntityId,
}: {
  documentId: string;
  mode?: "read" | "edit";
  /** W8 §F.4/6 — `?panel=conversation&c=<id>` makes the right drawer linkable and reloadable. */
  panel?: "conversation" | "chat" | "comments";
  panelEntityId?: string;
}) {
  const { t, i18n } = useTranslation();
  const { data: document, isLoading, isError } = useDocument(documentId);
  const { data: collection } = useAccessibleCollection(document?.collectionId ?? "");
  const can = usePolicy(document);
  const updateDocument = useUpdateDocument();
  const sculptState = useSculptLatest(documentId, Boolean(document));
  const context = useActionContext({ source: "contextmenu", activeEntityId: documentId });
  const setActiveEntityId = useShellStore((state) => state.setActiveEntityId);
  // W8 — the one right-hand slot. Which panel is in it is the whole mutual-exclusion rule.
  const rightDrawerKind = useShellStore((state) => state.rightDrawer.kind);
  const openRightDrawer = useShellStore((state) => state.openRightDrawer);
  const closeRightDrawer = useShellStore((state) => state.closeRightDrawer);

  const api = useApi();
  const navigate = useNavigate();
  const [draftTitle, setDraftTitle] = useState("");
  /**
   * W8 S6–S10 — the editor the drawer's panels act on.
   *
   * The scene holds it because the drawer is mounted here: kms needed an `EditorRegistry`
   * store only because its panels lived ABOVE the document context and had no other way to
   * reach the editor. Here there is a way, and it is a prop.
   */
  const [bodyEditor, setBodyEditor] = useState<Editor | null>(null);

  /**
   * W6.1 wire-up — the v2 engine's host api, once it mounts. Null on the classic lane and null
   * on the v2 lane until the editor is really there (no collaboration token ⇒ read-only text,
   * no editor), which is the state `documentPorts` turns into a visible reason rather than a
   * dead Apply button.
   */
  const [v2Host, setV2Host] = useState<EditorV2HostApi | null>(null);

  /**
   * Live widget values. The provider registry is host-global and both editing lanes render the
   * same `WidgetView`, so it is registered once here rather than per editor — and it only ever
   * produces a REFRESH affordance on a widget that declares a `sourceRef`. The stored payload
   * stays the record; a live value is a view of it, behind its own banner.
   */
  useEffect(() => registerQuoteProvider(api), [api]);
  const [sculptOpen, setSculptOpen] = useState(false);
  useDocumentTitle(document?.title ?? t("Document"));

  // Publishing the open document is what lets the command bar offer its actions.
  useEffect(() => {
    setActiveEntityId(documentId);
    return () => setActiveEntityId(null);
  }, [documentId, setActiveEntityId]);

  useEffect(() => {
    if (document) {
      setDraftTitle(document.title);
    }
  }, [document]);

  /**
   * The URL opens the drawer, once, on arrival — never on every render, or a person who closed
   * the drawer would find it reopening under them while the link still said `?panel=`.
   */
  const arrivedWith = useRef<string | null>(null);
  useEffect(() => {
    const wanted = panel ? `${panel}:${panelEntityId ?? ""}` : null;
    if (!panel || !wanted || arrivedWith.current === wanted) {
      return;
    }
    arrivedWith.current = wanted;
    openRightDrawer(panel, panelEntityId ?? null);
  }, [openRightDrawer, panel, panelEntityId]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  if (isError || !document) {
    return <Empty message={t("This document could not be loaded")} />;
  }

  const canUpdate = can[ABILITY.documentUpdate] === true;
  const editing = canUpdate && mode === "edit";
  const status = documentStatus(document);
  const statusLabel = STATUS_LABEL[status];
  /**
   * W6 — the second editing lane. A v2 document is edited in the CRDT room and has NO REST
   * save path, so the whole read/edit split below does not apply to it: no Save button, no
   * autosave, and no sculpt affordances (`SculptService` refuses a v2 document with
   * `apply_lane_unsupported` BEFORE it inserts an op row, and the propose-lane fallback
   * refuses again on resolve — so a proposal there could never be accepted).
   */
  const onV2 = document.editorLane === "v2";
  const sculptDisabled = onV2 || sculptState.data?.disabled === true;
  const latest = sculptState.data?.latest ?? null;
  const { body, hadHeading } = splitBody(document.text);

  const commitTitle = () => {
    const next = draftTitle.trim();
    if (next && next !== document.title) {
      updateDocument.mutate({ id: document.id, data: { title: next } });
    } else {
      setDraftTitle(document.title);
    }
  };

  // W7 — Taskize and "Remind me about this…" join the ⋯ menu from the SAME declarations ⌘K
  // reads, so the two lists cannot drift apart.
  const menuItems: MenuItem[] = [
    {
      type: "button",
      title: t("Chat"),
      icon: ChatCircleDots,
      onClick: () =>
        rightDrawerKind === "chat" ? closeRightDrawer() : openRightDrawer("chat", documentId),
    },
    {
      type: "button",
      title: t("Comments"),
      icon: Note,
      onClick: () =>
        rightDrawerKind === "comments"
          ? closeRightDrawer()
          : openRightDrawer("comments", documentId),
    },
    {
      type: "button",
      title: t("Debate this document"),
      icon: UsersThree,
      onClick: () =>
        void navigate({
          href: debateHref(
            { kind: "document", id: documentId },
            t("What should we do about «{{title}}»?", { title: document.title }),
          ),
        }),
    },
    {
      type: "route",
      title: t("History"),
      icon: ClockCounterClockwise,
      to: `/documents/${documentId}/history`,
    },
    {
      type: "route",
      title: t("Insights"),
      icon: ArrowSquareOut,
      to: `/documents/${documentId}/insights`,
    },
    { type: "separator" },
    ...actionsToMenuItems([...documentRowActions, ...documentAiActions], context),
  ];

  return (
    <div className="mx-auto flex w-full max-w-[960px] flex-col gap-5 px-5 py-6 sm:px-10 sm:py-8">
      <DocumentBreadcrumbs document={document} collection={collection} />

      <header className="flex flex-col gap-4 border-b border-divider pb-4">
        <div className="flex items-start gap-3">
          <span aria-hidden className="pt-1 text-3xl leading-none">
            {document.emoji ?? "📄"}
          </span>
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                value={draftTitle}
                aria-label={t("Title")}
                onChange={(event) => setDraftTitle(event.target.value)}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                }}
                className="w-full bg-transparent text-3xl font-semibold leading-tight tracking-tight text-text outline-none sm:text-4xl"
              />
            ) : (
              <h1 className="break-words text-3xl font-semibold leading-tight tracking-tight text-text sm:text-4xl">
                {document.title}
              </h1>
            )}
            <Text variant="small" className="mt-2 text-text-tertiary">
              {t("Updated {{when}}", { when: formatDateTime(document.updatedAt, i18n.language) })}
              {statusLabel ? ` · ${t(statusLabel)}` : null}
              {canUpdate ? null : ` · ${t("Read only")}`}
            </Text>
          </div>
          <StarButton documentId={documentId} />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {!editing && !onV2 && canUpdate ? (
            <Link
              to="/documents/$documentId/edit"
              params={{ documentId }}
              className="inline-flex h-8 items-center gap-1.5 rounded-sm border border-input-border bg-bg px-3 text-small font-medium text-text hover:bg-hover-bg"
            >
              <PencilSimple size={14} aria-hidden />
              {t("Edit")}
            </Link>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            icon={ChatsCircle}
            onClick={() => {
              closeRightDrawer();
              useThinkerDock.getState().show();
            }}
          >
            {t("Discuss with thinker")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={ChatsCircle}
            aria-pressed={rightDrawerKind === "conversation"}
            onClick={() =>
              rightDrawerKind === "conversation"
                ? closeRightDrawer()
                : openRightDrawer("conversation", null)
            }
          >
            {t("Conversations")}
          </Button>
          {!editing && !sculptDisabled ? (
            <Tooltip content={t("Sculpt with AI")}>
              <IconButton
                icon={Sparkle}
                aria-label={t("Sculpt with AI")}
                onClick={() => setSculptOpen(true)}
              />
            </Tooltip>
          ) : null}
          <a
            href="#document-connections"
            className="inline-flex h-7 items-center gap-1.5 rounded-sm px-2 text-small text-text-secondary hover:bg-hover-bg hover:text-text"
          >
            <LinkSimple size={14} aria-hidden />
            {t("Connections")}
          </a>
          <div className="ms-auto flex items-center gap-1">
            {collection && can[ABILITY.documentUpdate] ? (
              <PinButton documentId={documentId} collectionId={collection.id} />
            ) : null}
            <SubscribeButton kind="document" targetId={documentId} />
            <NudgeChip documentId={documentId} />
            <Menu
              align="end"
              ariaLabel={t("Document actions")}
              items={menuItems}
              trigger={<IconButton icon={DotsThree} aria-label={t("Document actions")} />}
            />
          </div>
        </div>
      </header>

      {/* Pinned above the body — renders nothing at all when the document has no tasks. */}
      <DocumentTasksPanel key={`tasks-${documentId}`} documentId={documentId} />

      {/* CWAY — the code this document is bridged to; renders nothing for a document that is not about code. */}
      <DescribesCodePanel documentId={documentId} />

      {/* W7 — the Taskize review tray and the QuickWhen picker. Both are portal-rendered and
          both render nothing until something asks for them; they live HERE rather than in the
          shell so they cannot outlive the document they are about. */}
      <TaskizeHost document={document} canUpdate={canUpdate} />
      <NudgePickerHost />

      {!editing && latest && (latest.status === "proposed" || latest.status === "degraded") ? (
        <SculptProposalCard documentId={documentId} latest={latest} canUpdate={canUpdate} />
      ) : null}

      {!editing && latest?.revertible ? (
        <SculptRevertRow documentId={documentId} latest={latest} canUpdate={canUpdate} />
      ) : null}

      {onV2 ? (
        <EditorV2Host document={document} canUpdate={canUpdate} onHostReady={setV2Host} />
      ) : editing ? (
        <DocumentBodyEditor
          document={document}
          body={body}
          hadHeading={hadHeading}
          title={draftTitle.trim() || document.title}
          onEditorReady={setBodyEditor}
        />
      ) : (
        <div className="py-1">
          {body ? (
            <DocumentEditor
              markdown={body}
              ariaLabel={t("Document content")}
              onEditorReady={setBodyEditor}
            />
          ) : canUpdate && !sculptDisabled ? (
            <ComposeCard
              documentId={documentId}
              // W7 — the New-document dialog's Compose hand-off. Reading it clears it.
              initialInstructions={takeCompose(documentId)}
            />
          ) : (
            <Text variant="secondary">{t("This document has no content yet.")}</Text>
          )}
        </div>
      )}

      {sculptDisabled ? null : (
        <SculptDialog
          documentId={documentId}
          open={sculptOpen}
          onOpenChange={setSculptOpen}
          canUpdate={canUpdate}
        />
      )}

      <DocumentConnections key={`connections-${documentId}`} documentId={documentId} />
      <EntityFlowsPanel kind="document" entityId={documentId} />
      <EditorLaneSwitch document={document} canUpdate={canUpdate} />

      {/* W8 — mounted INSIDE the document scene, which is why slate needs no `EditorRegistry`:
          the host already has the document, its policy and (once W6 lands them) the ports. */}
      <DocumentDrawer
        document={document}
        collectionId={document.collectionId}
        ports={documentPorts({
          editor: bodyEditor,
          lane: onV2 ? "v2" : "v1",
          editable: editing,
          canUpdate,
          v2Host,
        })}
      />
    </div>
  );
}

/**
 * The editing surface. The draft lives in the editor; Save routes the whole body through
 * the sculptor's deterministic write (`compose` + `providedMarkdown`, act lane) — the one
 * path that is safe whether or not the document's live state sits in a collaboration room.
 * Every save takes a server-side checkpoint, so the read view can offer Revert afterwards.
 */
function DocumentBodyEditor({
  document,
  body,
  hadHeading,
  title,
  onEditorReady,
}: {
  document: Document;
  body: string;
  hadHeading: boolean;
  title: string;
  /** W8 — the scene needs the same instance, to build the document AI layer's ports from it. */
  onEditorReady: (editor: Editor | null) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveDocumentBody();
  const draftRef = useRef(body);
  const [dirty, setDirty] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const api = useApi();

  /**
   * W10 §10 — turn a chosen (or pasted, or dropped) image into a URL the document can keep.
   *
   * Two steps on the wire, and the URL is the one from the FIRST: `attachments.create` reserves
   * the row and `attachments.upload` answers 204 with nothing in it. `documentId` is required by
   * the server, which is exactly why this lives here — the editor has no document to name.
   */
  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      try {
        const attachment = await api.attachments.upload({ file, documentId: document.id });
        return attachment.url;
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t("The image could not be uploaded"));
        throw error;
      }
    },
    [api, document.id, t],
  );

  // Stable identity: `DocumentEditor` calls this from an effect keyed on the callback, so an
  // inline arrow would re-run it on every render of this scene.
  const handleEditorReady = useCallback(
    (next: Editor | null) => {
      setEditor(next);
      onEditorReady(next);
    },
    [onEditorReady],
  );

  // The MERGED, SCOPED catalogue: the unscoped list is two client-side merges and structurally
  // cannot see a team/project/document-scoped action.
  const actions = useScopedAiActions({ documentId: document.id });
  /**
   * Who `@` may offer. The thinker registry comes back whole — the route takes no query — so
   * filtering and ranking happen client-side by design; people and documents are already
   * access-scoped server-side.
   *
   * A failed thinker read simply omits that section. No toast, no retry: somebody typing `@`
   * wants to finish their sentence, not to be told about a request they did not make.
   */
  const thinkers = useAvailableThinkers();
  const mentionable = useMentionSuggestions("");
  const selectionActions = useSelectionActions({
    editor,
    documentId: document.id,
    collectionId: document.collectionId,
    documentTitle: title,
    // `transform-selection` performs no document authorization of its own, so the AFFORDANCE
    // is what gates it — never widened into "transform any text you can post" (W6 R9).
    enabled: true,
  });

  const onSave = () => {
    if (save.isPending) {
      return;
    }
    const draft = draftRef.current.trim();
    // Keep the platform's leading-H1 convention: restore it when it was there (or the
    // document is brand new), leave documents that never had one alone.
    const markdown =
      hadHeading || document.text.trim().length === 0 ? `# ${title}\n\n${draft}` : draft;

    save.mutate(
      { documentId: document.id, markdown },
      {
        onSuccess: (outcome) => {
          if (outcome.status === "proposed") {
            toast.info(
              t("This document is collaboratively edited — your changes were staged as a proposal"),
            );
          } else {
            toast.success(t("Saved"));
          }
          void navigate({
            to: "/documents/$documentId",
            params: { documentId: document.id },
          });
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("Something went wrong"));
        },
      },
    );
  };

  // ⌘S / Ctrl+S saves — the one shortcut everybody tries first in an editor.
  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <DocumentEditor
        markdown={body}
        editable
        withToolbar
        autoFocus
        ariaLabel={t("Document content")}
        onEditorReady={handleEditorReady}
        onUploadImage={uploadImage}
        mentions={{
          thinkers: (thinkers.data ?? []).map((thinker) => ({
            id: thinker.id,
            name: thinker.name,
            description: thinker.description,
          })),
          documents: (mentionable.data?.documents ?? []).map((entry) => ({
            id: entry.id,
            title: entry.title,
          })),
          people: (mentionable.data?.users ?? []).map((person) => ({
            id: person.id,
            name: person.name,
          })),
        }}
        ai={{
          // W7 — the two client-side *Do* entries join the server's transform catalogue. They
          // are not transforms (they make records), so no catalogue row could configure them.
          actions: [...(actions.data ?? []), ...SELECTION_DO_ACTIONS],
          onSelectAction: (action) => void selectionActions.run(action),
          onDoAction: (action) => {
            const selected = editor
              ? editor.state.doc.textBetween(
                  editor.state.selection.from,
                  editor.state.selection.to,
                  " ",
                )
              : "";
            if (runDoAction(action.id, document.id, selected)) {
              return;
            }
            void selectionActions.run(action);
          },
          lastLane: selectionActions.lastLane,
          busy: selectionActions.busy,
        }}
        onChange={(markdown) => {
          draftRef.current = markdown;
          setDirty(true);
        }}
      />

      <SelectionActionsDrawer
        onAccept={selectionActions.accept}
        onRefine={selectionActions.refine}
        onDismiss={selectionActions.dismiss}
        refining={selectionActions.busy}
      />

      <div className="flex items-center justify-end gap-2 border-t border-divider pt-3">
        {dirty ? (
          <Text variant="small" className="me-auto text-text-tertiary">
            {t("Unsaved changes")}
          </Text>
        ) : null}
        <Link to="/documents/$documentId" params={{ documentId: document.id }}>
          <Button variant="ghost">{t("Cancel")}</Button>
        </Link>
        <Button variant="primary" disabled={save.isPending || !dirty} onClick={onSave}>
          {save.isPending ? t("Saving…") : t("Save")}
        </Button>
      </div>
    </div>
  );
}
